import { Controller } from 'stimulus';
import { calculateBillableTotals } from '@/js/shared/billables/totals';
import { formatCurrency } from '@/js/shared/billables/currency';
import { decimal, money } from '@/js/shared/billables/decimal';

export default class extends Controller {
  static targets = [
    'subtotal',
    'shipping',
    'shippingInput',
    'total',
    'balance',
    'recordPaymentButton',
  ];
  static outlets = ['billable-item', 'billable-discount', 'billable-tax', 'billable-deposit'];

  static values = {
    strategy: String,
    currencySymbol: String,
    currencySide: String,
    payments: String,
  };

  recalculate() {
    this.#render(calculateBillableTotals(this.strategyValue, this.#calculationInput));
  }

  get #calculationInput() {
    return {
      items: this.billableItemOutlets
        .filter((item) => !item.markedForDestruction)
        .map((item) => item.calculationInput),
      discount: this.hasBillableDiscountOutlet
        ? this.billableDiscountOutlet.calculationInput
        : null,
      tax: this.hasBillableTaxOutlet ? this.billableTaxOutlet.calculationInput : null,
      shipping: this.hasShippingInputTarget ? this.shippingInputTarget.value : '0',
      deposit: this.hasBillableDepositOutlet ? this.billableDepositOutlet.calculationInput : null,
      payments: this.paymentsValue || '0',
    };
  }

  get #currency() {
    return {
      symbol: this.currencySymbolValue,
      side: this.currencySideValue,
    };
  }

  #render(result) {
    const currency = this.#currency;

    if (this.hasSubtotalTarget) {
      this.subtotalTarget.textContent = formatCurrency(result.subtotal, currency, {
        useGrouping: false,
      });
    }
    if (this.hasShippingTarget) {
      this.shippingTarget.textContent = formatCurrency(result.shipping, currency, {
        useGrouping: false,
      });
    }
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = formatCurrency(result.total, currency);
    }
    if (this.hasBalanceTarget) {
      this.balanceTarget.textContent = formatCurrency(result.balance, currency);
    }
    if (this.hasRecordPaymentButtonTarget) {
      this.recordPaymentButtonTarget.classList.toggle('hide', decimal(result.balance).eq('0'));
    }

    if (this.hasBillableDiscountOutlet) {
      this.billableDiscountOutlet.renderCalculatedAmount({
        discount: result.discount,
        subtotal: result.subtotal,
        currency,
      });
    }

    if (this.hasBillableTaxOutlet) {
      this.billableTaxOutlet.renderCalculatedAmount({
        tax: result.tax,
        currency,
      });
    }

    if (this.hasBillableDepositOutlet) {
      this.billableDepositOutlet.renderCalculatedAmount({
        deposit: result.deposit,
        total: result.total,
        currency,
      });
    }

    this.dispatch('rendered', { detail: { total: result.total, payable: this.#payable(result) } });
  }

  // What the client pays next: the unpaid part of a requested deposit, otherwise the balance.
  #payable(result) {
    const owingDeposit = money(result.deposit || '0').minus(money(this.paymentsValue || '0'));

    return owingDeposit.gt('0') ? owingDeposit.toFixed(2) : result.balance;
  }
}
